import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Sparkles, Upload } from 'lucide-react';
import { Role } from '@nexa/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { RoleGate } from '@/auth/role-gate';
import {
  useCreateContract,
  useExtractContractFromPdf,
} from '@/lib/contracts/use-contracts';
import type { CreateContractInput, ExtractionProvenance } from '@/lib/contracts/types';
import { ContractForm } from './contract-form';
import { ExtractionBanner } from './extraction-banner';

type Mode = 'manual' | 'pdf';

export function ContractNewRoute() {
  const { t } = useTranslation('network');
  const navigate = useNavigate();
  const create = useCreateContract();
  const extract = useExtractContractFromPdf();
  const [mode, setMode] = useState<Mode>('manual');
  const [draft, setDraft] = useState<Partial<CreateContractInput> | null>(null);
  const [provenance, setProvenance] = useState<ExtractionProvenance | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const onSave = async (input: CreateContractInput) => {
    const saved = await create.mutateAsync(input);
    navigate(`/contracts/${encodeURIComponent(saved.contractUrn)}`);
  };

  return (
    <RoleGate min={Role.OPS_SUPERVISOR} hideWhenDenied>
      <main className="density-page mx-auto flex max-w-2xl flex-col gap-4 p-6">
        <header>
          <h1 className="text-2xl font-semibold">{t('contracts.new.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('contracts.new.subtitle')}</p>
        </header>

        <div className="flex gap-2" role="tablist" aria-label={t('contracts.new.modeLabel')}>
          <Button
            role="tab"
            aria-selected={mode === 'manual'}
            variant={mode === 'manual' ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setMode('manual');
              setDraft(null);
              setProvenance(null);
            }}
          >
            {t('contracts.new.modeManual')}
          </Button>
          <Button
            role="tab"
            aria-selected={mode === 'pdf'}
            variant={mode === 'pdf' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('pdf')}
          >
            <Sparkles className="mr-1 h-3 w-3" aria-hidden />
            {t('contracts.new.modePdf')}
          </Button>
        </div>

        {mode === 'manual' ? (
          <Card>
            <CardContent className="p-6">
              <ContractForm
                submitLabel={t('contracts.new.submit')}
                pending={create.isPending}
                onSubmit={onSave}
              />
            </CardContent>
          </Card>
        ) : draft ? (
          <Card>
            <CardContent className="flex flex-col gap-4 p-6">
              {provenance ? <ExtractionBanner provenance={provenance} /> : null}
              <ContractForm
                initial={draft}
                submitLabel={t('contracts.new.draftSubmit')}
                pending={create.isPending}
                onSubmit={onSave}
              />
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDraft(null);
                    setProvenance(null);
                  }}
                >
                  {t('contracts.new.draftReject')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="flex flex-col gap-3 p-6">
              <label className="flex flex-col gap-2 text-sm">
                <span className="font-medium text-foreground/80">{t('contracts.new.pdfLabel')}</span>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  aria-label={t('contracts.new.pdfLabel')}
                />
              </label>
              {extract.isError ? (
                <p className="text-sm text-destructive">{t('contracts.new.pdfError')}</p>
              ) : null}
              <div className="flex justify-end">
                <Button
                  disabled={!file || extract.isPending}
                  onClick={async () => {
                    if (!file) return;
                    const result = await extract.mutateAsync(file);
                    setDraft(result.draft);
                    setProvenance(result.provenance);
                  }}
                >
                  <Upload className="mr-1 h-3 w-3" aria-hidden />
                  {extract.isPending ? t('contracts.new.extracting') : t('contracts.new.extract')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </RoleGate>
  );
}
